import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { RegisterModal } from '../components/RegisterModal';
import { ToolFeedback } from '../components/ToolFeedback';
import { EmailVerificationWall } from '../components/EmailVerificationWall';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Calculator,
  Loader2,
  CheckCircle2,
  CalendarIcon,
  DollarSign,
  AlertTriangle,
  TrendingDown,
  Lock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const terminationTypes = [
  { value: 'despido_injustificado', label: 'Despido Injustificado' },
  { value: 'renuncia_voluntaria', label: 'Renuncia Voluntaria' },
  { value: 'rescision_justificada', label: 'Rescisión por Causa Justificada (despido con causa)' },
  { value: 'retiro_justificado', label: 'Retiro Voluntario Justificado (Art. 51 LFT)' },
];

const geographicZones = [
  { value: 'general', label: 'Zona General' },
  { value: 'frontera_norte', label: 'Zona Libre de la Frontera Norte' },
];

// Concepts still gated behind Pro once resultados-difuminados replaced el límite de usos
// (ver instrucción de sept. 2026) — el resto del desglose se muestra siempre en claro.
const BLURRED_BREAKDOWN_KEYS = new Set([
  'vacaciones_proporcionales',
  'prima_vacacional',
  'veinte_dias_por_ano',
  'prima_antiguedad',
]);

// No confiar en blur() de CSS: sobre texto grande/negrita en fondo oscuro (Total Estimado)
// el contraste sobrevive al blur-sm y el número se lee a simple vista (bug real en prod,
// sept. 2026). En vez de difuminar el valor real, se sustituye por este marcador fijo —
// no hay dígitos reales en el DOM que se puedan leer con ningún nivel de blur.
const MASKED_CURRENCY = '$•••,•••';

export default function SeverancePage() {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const pendingSubmitRef = useRef(false);
  const [fiscalInfo, setFiscalInfo] = useState({ salario_minimo: { general: 315.04, frontera_norte: 440.87 } });

  const [formData, setFormData] = useState({
    salary: '',
    termination_type: '',
    geographic_zone: 'general',
  });

  const isPro = user?.plan === 'pro';
  const isBlurred = !!result && !isPro;

  useEffect(() => {
    const fetchFiscalInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/fiscal/info`);
        if (res.ok) setFiscalInfo(await res.json());
      } catch {}
    };
    fetchFiscalInfo();
  }, []);

  // Registra en usage_limit_events cada vez que un usuario no-Pro ve la versión
  // difuminada del resultado (reemplaza a "limit_reached" para esta herramienta, que ya
  // no aplica: no hay límite de usos, el difuminado es el mecanismo permanente).
  useEffect(() => {
    if (isBlurred) {
      authFetch(`${API_URL}/api/usage-limit/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: 'severance', event: 'blurred_result_view' }),
      }).catch(() => { /* non-blocking */ });
    }
  }, [result, isPro]);

  const trackUpgradeClick = () => {
    authFetch(`${API_URL}/api/usage-limit/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool: 'severance', event: 'upgrade_click' }),
    }).catch(() => { /* non-blocking */ });
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    return startDate && endDate && formData.salary && formData.termination_type;
  };

  const executeSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        salary: parseFloat(formData.salary),
        termination_type: formData.termination_type,
      };

      const response = await authFetch(`${API_URL}/api/calculations/severance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al calcular');
      }

      const data = await response.json();
      setResult(data);
      toast.success('Cálculo completado');
    } catch (error) {
      toast.error(error.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;

    if (!isAuthenticated) {
      pendingSubmitRef.current = true;
      setShowRegisterModal(true);
      return;
    }

    await executeSubmit();
  };

  const handleRegisterSuccess = (registeredUser) => {
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      // Cuenta recién creada, aún sin verificar: el muro se mostrará en el siguiente
      // render en vez de ejecutar el cálculo pendiente.
      if (registeredUser?.email_verified === false) return;
      executeSubmit();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getTerminationLabel = (type) => {
    const found = terminationTypes.find((t) => t.value === type);
    return found?.label || type;
  };

  return (
    <Layout>
      <PageSEO
        title="Calculadora de Liquidación México (LFT) | Klarvi"
        description="Calcula tu liquidación y finiquito en México según la Ley Federal del Trabajo (LFT). Indemnización, prima de antigüedad y más, gratis y en minutos."
        canonical="https://klarvi.org/severance"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Calculadora de Liquidación - Klarvi",
          "url": "https://klarvi.org/severance",
          "description": "Calcula tu liquidación y finiquito en México según la Ley Federal del Trabajo (LFT).",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "es-MX",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "MXN" },
        }}
      />
      {isAuthenticated && user?.email_verified === false ? (
        <EmailVerificationWall />
      ) : (
      <>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="severance-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">
            Calculadora de Liquidación
          </h1>
          <p className="mt-2 text-muted-foreground">
            Estima tu liquidación y finiquito según la Ley Federal del Trabajo.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Calculator className="w-5 h-5 text-[#2563EB]" />
                Datos del Empleo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Fecha de Inicio</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="start-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? (
                            format(startDate, 'dd/MM/yyyy', { locale: es })
                          ) : (
                            <span className="text-muted-foreground">Seleccionar</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          disabled={(date) => date > new Date()}
                          captionLayout="dropdown-buttons"
                          fromYear={1970}
                          toYear={new Date().getFullYear()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Término</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="end-date-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? (
                            format(endDate, 'dd/MM/yyyy', { locale: es })
                          ) : (
                            <span className="text-muted-foreground">Seleccionar</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={(date) => startDate && date < startDate}
                          captionLayout="dropdown-buttons"
                          fromYear={1970}
                          toYear={new Date().getFullYear()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Salary */}
                <div className="space-y-2">
                  <Label htmlFor="salary">Salario Mensual Bruto (MXN)</Label>
                  <Input
                    id="salary"
                    type="number"
                    value={formData.salary}
                    onChange={(e) => updateField('salary', e.target.value)}
                    placeholder="45000"
                    data-testid="severance-salary-input"
                  />
                </div>

                {/* Termination Type */}
                <div className="space-y-2">
                  <Label htmlFor="termination_type">Tipo de Terminación</Label>
                  <Select
                    value={formData.termination_type}
                    onValueChange={(v) => updateField('termination_type', v)}
                  >
                    <SelectTrigger data-testid="termination-type-select">
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {terminationTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Geographic Zone */}
                <div className="space-y-2">
                  <Label htmlFor="geographic_zone">Zona Geográfica</Label>
                  <Select
                    value={formData.geographic_zone}
                    onValueChange={(v) => updateField('geographic_zone', v)}
                  >
                    <SelectTrigger data-testid="geographic-zone-select">
                      <SelectValue placeholder="Selecciona la zona" />
                    </SelectTrigger>
                    <SelectContent>
                      {geographicZones.map((z) => (
                        <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Afecta el tope de la prima de antigüedad. Salario mínimo 2026: General ${fiscalInfo.salario_minimo.general.toFixed(2)}/día, Frontera Norte ${fiscalInfo.salario_minimo.frontera_norte.toFixed(2)}/día.
                  </p>
                </div>

                {/* Termination type description */}
                {formData.termination_type && (
                  <div className="p-4 bg-muted rounded-lg text-sm" data-testid="termination-info">
                    {formData.termination_type === 'despido_injustificado' && (
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• <strong>Indemnización constitucional:</strong> 3 meses de salario</li>
                        <li>• <strong>20 días por año trabajado</strong></li>
                        <li>• <strong>Prima de antigüedad:</strong> 12 días/año (tope: 2x salario mínimo), desde el primer año</li>
                        <li>• <strong>Finiquito:</strong> Aguinaldo, vacaciones y prima vacacional proporcionales</li>
                      </ul>
                    )}
                    {formData.termination_type === 'renuncia_voluntaria' && (
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• <strong>Solo finiquito:</strong> Aguinaldo, vacaciones y prima vacacional proporcionales</li>
                        <li>• Sin indemnización</li>
                        <li>• <strong>Prima de antigüedad:</strong> solo si tienes 15+ años de antigüedad</li>
                      </ul>
                    )}
                    {formData.termination_type === 'rescision_justificada' && (
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• <strong>Solo finiquito:</strong> Aguinaldo, vacaciones y prima vacacional proporcionales</li>
                        <li>• Sin indemnización</li>
                        <li>• <strong>Prima de antigüedad:</strong> 12 días/año (tope: 2x salario mínimo), desde el primer año</li>
                      </ul>
                    )}
                    {formData.termination_type === 'retiro_justificado' && (
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• <strong>Indemnización constitucional:</strong> 3 meses de salario</li>
                        <li>• <strong>20 días por año trabajado</strong></li>
                        <li>• <strong>Prima de antigüedad:</strong> 12 días/año (tope: 2x salario mínimo), desde el primer año</li>
                        <li>• <strong>Finiquito:</strong> Aguinaldo, vacaciones y prima vacacional proporcionales</li>
                      </ul>
                    )}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                  disabled={!canSubmit() || loading}
                  data-testid="calculate-severance-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Calcular
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            {result ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <Card className="border animate-scale-in">
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Finiquito Estimado</p>
                      <p className="text-2xl font-bold text-[#0F172A] mt-1">
                        {formatCurrency(result.estimated_settlement)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border animate-scale-in" style={{ animationDelay: '50ms' }}>
                    <CardContent className="p-4">
                      <p className="text-sm text-muted-foreground">Liquidación Estimada</p>
                      <p className="text-2xl font-bold text-[#2563EB] mt-1">
                        {isBlurred ? MASKED_CURRENCY : formatCurrency(result.estimated_severance)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Total */}
                <Card className="border bg-[#0F172A] text-white animate-slide-up" style={{ animationDelay: '100ms' }}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-slate-300">Total Estimado</p>
                        <p className="text-3xl font-bold mt-1">
                          {isBlurred ? MASKED_CURRENCY : formatCurrency(result.estimated_severance + result.estimated_settlement)}
                        </p>
                      </div>
                      <DollarSign className="w-10 h-10 text-[#2563EB]" />
                    </div>
                  </CardContent>
                </Card>

                {/* Breakdown */}
                <Card className="border animate-slide-up" style={{ animationDelay: '150ms' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Manrope']">
                      Desglose por Concepto
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Tipo: {getTerminationLabel(result.input_data?.termination_type)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {Object.entries(result.breakdown).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-2 border-b border-border last:border-0">
                          <span className="text-muted-foreground">
                            {key === 'aguinaldo_proporcional' ? 'Aguinaldo proporcional'
                              : key === 'vacaciones_proporcionales' ? 'Vacaciones proporcionales'
                              : key === 'prima_vacacional' ? 'Prima vacacional'
                              : key === 'indemnizacion_constitucional' ? 'Indemnización constitucional (3 meses)'
                              : key === 'veinte_dias_por_ano' ? '20 días por año trabajado'
                              : key === 'prima_antiguedad' ? 'Prima de antigüedad'
                              : key.replace(/_/g, ' ')}
                          </span>
                          <span className="font-medium">
                            {isBlurred && BLURRED_BREAKDOWN_KEYS.has(key) ? MASKED_CURRENCY : formatCurrency(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Pro upsell — reemplaza el límite de usos para esta herramienta */}
                {isBlurred && (
                  <div className="flex flex-wrap items-center justify-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="severance-pro-upsell">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span>Ya viste una parte real de tu liquidación. Con Pro ves el desglose completo y el total exacto — sin límite de cálculos.</span>
                    <Link to="/precios" onClick={trackUpgradeClick}>
                      <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="severance-upgrade-btn">
                        Actualizar a Pro
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="legal-disclaimer flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Este cálculo es una estimación basada en la Ley Federal del Trabajo. 
                    El monto real puede variar según tu contrato, convenios colectivos y 
                    circunstancias específicas. Consulta con un abogado laboral para casos complejos.
                  </span>
                </div>
                <ToolFeedback tool="calculadora_liquidacion" />
              </>
            ) : (
              <Card className="border h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <TrendingDown className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-[#0F172A] font-['Manrope']">
                    Calcula tu liquidación
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Completa los datos para estimar tu finiquito y liquidación.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <RegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onSuccess={handleRegisterSuccess}
      />
      </>
      )}
    </Layout>
  );
}